/**
 * The entry gate — decides whether to show Register/Login/Guest (`EntryView`)
 * or the game itself, based on whether a Supabase session already exists.
 *
 * Critical backward-compat path: every player who already has a session
 * today (anonymous or Google-linked) sees NOTHING different — `peekSession()`
 * finds it and `children` (the `GameProvider`/`App` tree) renders immediately,
 * exactly as before this feature existed. Only a visitor with no session at
 * all sees the new screens.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useGameActions } from './GameProvider';
import type { SupabaseAuth } from './supabase-auth';
import { EntryView, RecoveryScreen } from '../views/EntryView';

type GateState = 'checking' | 'gate' | 'in' | 'recovery';

const PendingNameContext = createContext<{ name: string | null; clear: () => void } | null>(null);

export interface AuthGateProps {
  auth: SupabaseAuth;
  logo?: ReactNode;
  children: ReactNode;
}

export function AuthGate({ auth, logo, children }: AuthGateProps) {
  const [state, setState] = useState<GateState>('checking');
  const [pendingName, setPendingName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void auth.peekSession().then((session) => {
      if (cancelled) return;
      setState((current) => (current === 'recovery' ? current : session ? 'in' : 'gate'));
    });
    // Fires when a password-reset email link is opened — Supabase's SDK
    // auto-detects the recovery token in the URL and emits this event,
    // regardless of what `peekSession()` above found.
    const unsubscribe = auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setState('recovery');
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [auth]);

  if (state === 'checking') return null; // one frame while the session check resolves

  if (state === 'recovery') {
    return <RecoveryScreen auth={auth} onDone={() => setState('in')} />;
  }

  if (state === 'gate') {
    return (
      <EntryView
        auth={auth}
        logo={logo}
        onEnter={(name) => {
          setPendingName(name);
          setState('in');
        }}
      />
    );
  }

  return (
    <PendingNameContext.Provider value={{ name: pendingName, clear: () => setPendingName(null) }}>
      {children}
    </PendingNameContext.Provider>
  );
}

/**
 * Dispatches the commander name chosen during Register/Guest exactly once,
 * then clears it — a no-op for Login/Google, which never set a pending name.
 * Mount inside `<GameProvider>` (needs `useGameActions()`), alongside `<App/>`.
 */
export function ApplyPendingName() {
  const ctx = useContext(PendingNameContext);
  const actions = useGameActions();
  const applied = useRef(false);

  useEffect(() => {
    if (!ctx?.name || applied.current) return;
    applied.current = true;
    actions.renameCommander(ctx.name);
    ctx.clear();
  }, [ctx, actions]);

  return null;
}
